import { AlertCircle, Check, Gamepad2, GripVertical, Plus, Search, Trash2, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { FormEvent } from "react";
import { AppContainer } from "../components/AppContainer";
import type { SwitcherUser } from "../components/AppSwitcher";
import { DeleteConfirmDialog } from "../components/DeleteConfirmDialog";
import { usePointerReorder } from "../components/ReorderHandle";
import { realtimeFetch } from "../realtime/client";
import { useRealtime } from "../realtime/useRealtime";
import { filterDinnerMeals } from "../dinner/search";
import { createDinnerLiveSaveQueue } from "../dinner/DinnerApp";
import type { DinnerLiveSaveQueue } from "../dinner/DinnerApp";
import type { Activity, Game } from "./domain";
import type { GameSnapshot } from "./domain";

type ErrorBody = { error?: { message?: string } };
const goTo = (path: string) => window.location.assign(path);

async function responseError(response: Response) {
  const body = await response.json().catch(() => null) as ErrorBody | null;
  return body?.error?.message ?? "Something went wrong. Please try again.";
}

interface ChecklistConfig {
  singular: "activity";
  plural: "activities";
  label: "Activity";
  apiPath: "/api/activities";
  listPath: "/activities";
}

const activityConfig: ChecklistConfig = {
  singular: "activity",
  plural: "activities",
  label: "Activity",
  apiPath: "/api/activities",
  listPath: "/activities",
};

type ChecklistItem = Activity;

export function ActivitiesChecklistRow({ item, selected, doing, dragging, editPath, onSelect }: { item: ChecklistItem; selected: boolean; doing: boolean; dragging: boolean; editPath: string; onSelect: () => void }) {
  return (
    <li data-reorder-id={item.id} className={`chore-row-shell${dragging ? " is-dragging" : ""}`}>
      <div className="chore-row">
        <button className="reorder-grip" type="button" aria-label={`Move ${item.name}`}><GripVertical size={20} /></button>
        <label className="chore-check-wrap">
          <input className="chore-checkbox" type="checkbox" checked={selected} onChange={onSelect} disabled={doing} aria-label={`Select ${item.name}`} />
          <span className="custom-check"><Check size={18} /></span>
        </label>
        <button className="chore-row-target" type="button" onClick={() => goTo(editPath)}><span className="chore-name">{item.name}</span></button>
      </div>
    </li>
  );
}

function ChecklistPage({ config, user, onSignOut }: { config: ChecklistConfig; user: SwitcherUser; onSignOut: () => Promise<void> }) {
  const [items, setItems] = useState<ChecklistItem[]>([]);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(true);
  const [doing, setDoing] = useState(false);
  const [error, setError] = useState("");
  const pendingRealtimeRefresh = useRef(false);

  async function load(spinner = true) {
    if (spinner) setLoading(true);
    try {
      const response = await fetch(config.apiPath);
      if (!response.ok) throw new Error(await responseError(response));
      const data = await response.json() as { activities?: Activity[] };
      const loadedItems = data[config.plural] ?? [];
      setItems(loadedItems);
      setSelected(current => new Set([...current].filter(id => loadedItems.some(item => item.id === id))));
      setError("");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : `Could not load ${config.plural}.`);
    } finally {
      if (spinner) setLoading(false);
    }
  }

  useEffect(() => { void load(); }, []);
  useRealtime("activities", signal => {
    if (signal.type !== "sync" && signal.event.resource !== config.singular) return;
    if (reorder.isBusy()) {
      pendingRealtimeRefresh.current = true;
      return;
    }
    void load(false);
  });

  async function persistOrder(next: ChecklistItem[]) {
    const response = await realtimeFetch(`${config.apiPath}/order`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ids: next.map(item => item.id) }),
    });
    if (!response.ok) throw new Error(await responseError(response));
    flushRealtime();
  }

  function flushRealtime() {
    if (!pendingRealtimeRefresh.current) return;
    pendingRealtimeRefresh.current = false;
    void load(false);
  }

  const reorder = usePointerReorder({
    items,
    rowSelector: "[data-reorder-id]",
    onChange: setItems,
    onCommit: persistOrder,
    onError: caught => {
      setError(caught instanceof Error ? caught.message : `Could not reorder ${config.plural}.`);
      flushRealtime();
    },
    onCancel: flushRealtime,
  });

  async function done() {
    if (!selected.size || doing) return;
    setDoing(true);
    try {
      const response = await realtimeFetch(`${config.apiPath}/done`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ids: [...selected] }),
      });
      if (!response.ok) throw new Error(await responseError(response));
      setSelected(new Set());
      await load(false);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : `Could not complete selected ${config.plural}.`);
    } finally {
      setDoing(false);
    }
  }

  const action = (
    <>
      {selected.size > 0 && <button className="show-all-button" type="button" disabled={doing} aria-busy={doing} aria-label={`Complete ${selected.size} selected ${config.plural}`} onClick={() => void done()}>{doing ? "…" : "Done"}</button>}
      <a className="add-chore-button" href={`${config.listPath}/new`} aria-label={`Add ${config.singular}`}><Plus size={25} /></a>
    </>
  );

  return (
    <AppContainer className="chores-shell app-activities" currentDestination="activities" user={user} onSignOut={onSignOut} asHeading actions={action}>
      {error && <div className="inline-error" role="alert"><AlertCircle size={18} />{error}<button type="button" onClick={() => void load(false)}>Try again</button></div>}
      {loading
        ? <div className="chores-state">Loading {config.plural}…</div>
        : items.length
          ? <ul ref={reorder.listRef} className="chores-list" aria-label="Activities">{items.map(item => <ActivitiesChecklistRow key={item.id} item={item} selected={selected.has(item.id)} doing={doing} dragging={reorder.draggingId === item.id} editPath={`${config.listPath}/${item.id}/edit`} onSelect={() => setSelected(current => { const next = new Set(current); next.has(item.id) ? next.delete(item.id) : next.add(item.id); return next; })} />)}</ul>
          : <section className="empty-chores"><span className="empty-check"><Check size={29} /></span><h2>No {config.plural} yet</h2><p>Add a {config.singular} to get started.</p></section>}
    </AppContainer>
  );
}

export function GameRow({ game }: { game: Game }) {
  return (
    <li className="dinner-row-shell">
      <a className="dinner-row dinner-edit-target" href={`/games/${game.id}/edit`} aria-label={`Edit ${game.name}`}><span className="dinner-name">{game.name}</span></a>
    </li>
  );
}

export function GameCandidate({ game, viewer, confirmationCount, requiredConfirmations, busy, onSkip, onConfirm, onUnconfirm }: {
  game: Game;
  viewer: GameSnapshot["viewer"];
  confirmationCount: number;
  requiredConfirmations: number;
  busy: boolean;
  onSkip: (game: Game) => void;
  onConfirm: (game: Game) => void;
  onUnconfirm: (game: Game) => void;
}) {
  const skipDisabled = viewer.skipped || game.skips >= 3 || busy;
  return (
    <section className="dinner-candidate" aria-labelledby="game-candidate-name">
      <h2 id="game-candidate-name">{game.name}</h2>
      {game.notes && <p className="dinner-candidate-notes">{game.notes}</p>}
      <div className="dinner-candidate-actions">
        <button className="dinner-skip-button" type="button" disabled={skipDisabled} aria-label={`Skip ${game.name}`} onClick={() => onSkip(game)}>Skip</button>
        {viewer.confirmed
          ? <button className="dinner-eat-button is-confirmed" type="button" disabled={busy} aria-label={`Undo confirmation for ${game.name}`} onClick={() => onUnconfirm(game)}>Undo Play</button>
          : <button className="dinner-eat-button" type="button" disabled={busy} aria-label={`Confirm playing ${game.name}`} onClick={() => onConfirm(game)}>Play</button>}
      </div>
      <p className="dinner-confirmation-status" aria-live="polite">Waiting for {confirmationCount} of {requiredConfirmations} player{requiredConfirmations === 1 ? "" : "s"}</p>
    </section>
  );
}

const EMPTY_GAME_SNAPSHOT: GameSnapshot = { games: [], viewer: { skipped: false, confirmed: false }, confirmationCount: 0, requiredConfirmations: 1, mutationResult: null };

function GamesPage({ user, onSignOut }: { user: SwitcherUser; onSignOut: () => Promise<void> }) {
  const [snapshot, setSnapshot] = useState<GameSnapshot>(EMPTY_GAME_SNAPSHOT);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyIds, setBusyIds] = useState<Set<number>>(new Set());
  const [query, setQuery] = useState("");

  async function load(spinner = true) {
    if (spinner) setLoading(true);
    try {
      const response = await fetch("/api/games");
      if (!response.ok) throw new Error(await responseError(response));
      const payload = await response.json() as GameSnapshot;
      setSnapshot(payload);
      if (spinner) setError("");
    } catch (caught) {
      if (spinner) setError(caught instanceof Error ? caught.message : "Could not load games.");
    } finally {
      if (spinner) setLoading(false);
    }
  }

  useEffect(() => { void load(true); }, []);
  useRealtime("games", signal => {
    if (signal.type !== "sync" && signal.event.resource !== "game") return;
    void load(false);
  });

  function setBusy(id: number, busy: boolean) {
    setBusyIds(current => {
      const next = new Set(current);
      if (busy) next.add(id); else next.delete(id);
      return next;
    });
  }

  async function mutateCandidate(game: Game, method: "POST" | "DELETE", action: "confirm" | "skip") {
    setBusy(game.id, true);
    setError("");
    try {
      const response = await realtimeFetch(`/api/games/${game.id}/${action}`, { method });
      if (!response.ok) throw new Error(await responseError(response));
      setSnapshot(await response.json() as GameSnapshot);
      setBusyIds(new Set());
    } catch (caught) {
      setBusy(game.id, false);
      setError(caught instanceof Error ? caught.message : `Could not update ${game.name}.`);
    }
  }

  const games = snapshot.games;
  const candidate = games[0];
  const filteredGames = filterDinnerMeals(games, query);
  return (
    <AppContainer className="chores-shell app-games" currentDestination="games" user={user} onSignOut={onSignOut} asHeading actions={<a className="add-dinner-button" href="/games/new" aria-label="Add game"><Plus size={25} strokeWidth={2.4} /></a>}>
      {error && !loading && games.length > 0 && <div className="inline-error" role="alert"><AlertCircle size={18} />{error}</div>}
      {!loading && candidate && <GameCandidate game={candidate} viewer={snapshot.viewer} confirmationCount={snapshot.confirmationCount} requiredConfirmations={snapshot.requiredConfirmations} busy={busyIds.size > 0} onSkip={game => void mutateCandidate(game, "POST", "skip")} onConfirm={game => void mutateCandidate(game, "POST", "confirm")} onUnconfirm={game => void mutateCandidate(game, "DELETE", "confirm")} />}
      {games.length > 0 && <div className="dinner-search"><Search size={19} aria-hidden="true" /><input name="game-search" type="search" autoComplete="off" autoCapitalize="none" value={query} onChange={event => setQuery(event.target.value)} placeholder="Search games" aria-label="Search games" />{query && <button className="search-clear-button" type="button" onClick={() => setQuery("")} aria-label="Clear game search"><X size={17} aria-hidden="true" /></button>}</div>}
      {loading && games.length === 0
        ? <div className="dinner-state" role="status"><span className="loading-dot" />Loading games…</div>
        : error && games.length === 0
          ? <div className="dinner-state error-state" role="alert"><AlertCircle size={22} aria-hidden="true" /><p>{error}</p><button className="text-button" type="button" onClick={() => void load(true)}>Try again</button></div>
          : games.length === 0
            ? <section className="empty-dinners"><span className="empty-dinner-icon"><Gamepad2 size={29} /></span><h2>No games yet</h2><p>Add a game to start your list.</p><a className="primary-button" href="/games/new">Add game</a></section>
            : filteredGames.length === 0
              ? <div className="dinner-no-results" role="status">No games match your search.</div>
              : <ul className="dinner-list" aria-label="Games">{filteredGames.map(game => <GameRow key={game.id} game={game} />)}</ul>}
    </AppContainer>
  );
}

function DeleteChecklistDialog({ config, name, busy, onCancel, onConfirm }: { config: ChecklistConfig; name: string; busy: boolean; onCancel: () => void; onConfirm: () => void }) {
  return <DeleteConfirmDialog title={<>Delete “{name || `this ${config.singular}`}”?</>} titleId={`delete-${config.singular}-title`} description={`This permanently deletes the ${config.singular}. This cannot be undone.`} descriptionId={`delete-${config.singular}-description`} busy={busy} onCancel={onCancel} onConfirm={onConfirm} confirmLabel={`Delete ${config.singular}`} />;
}

export function DeleteActivityDialog(props: { name: string; busy: boolean; onCancel: () => void; onConfirm: () => void }) {
  return <DeleteChecklistDialog config={activityConfig} {...props} />;
}

function ChecklistEditor({ config, id, user, onSignOut }: { config: ChecklistConfig; id: number | null; user: SwitcherUser; onSignOut: () => Promise<void> }) {
  const editing = id !== null;
  const [name, setName] = useState("");
  const [notes, setNotes] = useState("");
  const [loaded, setLoaded] = useState(!editing);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  const queued = useRef(Promise.resolve());
  const revision = useRef(0);

  useEffect(() => {
    if (!editing) return;
    void (async () => {
      try {
        const response = await fetch(`${config.apiPath}/${id}`);
        if (!response.ok) throw new Error(await responseError(response));
        const data = await response.json() as { activity?: Activity };
        const item = data[config.singular];
        if (!item) throw new Error(`${config.label} not found.`);
        setName(item.name);
        setNotes(item.notes);
      } catch (caught) {
        setError(caught instanceof Error ? caught.message : `Could not load this ${config.singular}.`);
      } finally {
        setLoaded(true);
      }
    })();
  }, [config, editing, id]);

  function valid(nextName: string, nextNotes: string) {
    return nextName.trim().length > 0 && nextName.trim().length <= 120 && nextNotes.trim().length <= 2_000;
  }

  function schedule(nextName: string, nextNotes: string) {
    if (!editing || !valid(nextName, nextNotes)) return;
    const current = ++revision.current;
    setStatus("Saving…");
    queued.current = queued.current.then(async () => {
      const response = await realtimeFetch(`${config.apiPath}/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: nextName, notes: nextNotes }),
      });
      if (!response.ok) throw new Error(await responseError(response));
      if (current === revision.current) setStatus("Saved");
    }).catch(caught => {
      if (current === revision.current) {
        setStatus("");
        setError(caught instanceof Error ? caught.message : "Could not save changes.");
      }
    });
  }

  function change(field: "name" | "notes", value: string) {
    const nextName = field === "name" ? value : name;
    const nextNotes = field === "notes" ? value : notes;
    if (field === "name") setName(value); else setNotes(value);
    setError("");
    window.clearTimeout(timer.current);
    if (editing) timer.current = window.setTimeout(() => schedule(nextName, nextNotes), 500);
  }

  async function create(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!valid(name, notes)) {
      setError(`Enter a ${config.singular} name.`);
      return;
    }
    try {
      const response = await realtimeFetch(config.apiPath, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, notes }),
      });
      if (!response.ok) throw new Error(await responseError(response));
      goTo(config.listPath);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : `Could not add ${config.singular}.`);
    }
  }

  async function remove() {
    if (!editing || deleting) return;
    setDeleting(true);
    setError("");
    window.clearTimeout(timer.current);
    revision.current += 1;
    await queued.current;
    try {
      const response = await realtimeFetch(`${config.apiPath}/${id}`, { method: "DELETE" });
      if (!response.ok) throw new Error(await responseError(response));
      goTo(config.listPath);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : `Could not delete ${config.singular}.`);
      setConfirmDelete(false);
      setDeleting(false);
    }
  }

  async function back() {
    window.clearTimeout(timer.current);
    await queued.current;
    goTo(config.listPath);
  }

  if (!loaded) return <AppContainer className="editor-shell app-activities" currentDestination="activities" user={user} onSignOut={onSignOut} backHref={config.listPath} title={`Edit ${config.singular}`} asHeading><div className="chores-state">Loading {config.singular}…</div></AppContainer>;

  return (
    <AppContainer className="editor-shell app-activities" currentDestination="activities" user={user} onSignOut={onSignOut} backHref={config.listPath} onBack={editing ? () => void back() : undefined} title={editing ? `Edit ${config.singular}` : `New ${config.singular}`} asHeading>
      <form className="chore-form" autoComplete="off" onSubmit={create}>
        {error && <div className="form-error" role="alert">{error}</div>}
        <label className="form-field"><span>Name</span><input name={`activities-${config.singular}-name`} autoComplete="off" autoCapitalize="sentences" autoFocus={!editing} value={name} maxLength={120} onChange={event => change("name", event.target.value)} /></label>
        <label className="form-field"><span>Notes</span><textarea name={`activities-${config.singular}-notes`} autoComplete="off" autoCapitalize="sentences" rows={4} value={notes} maxLength={2_000} onChange={event => change("notes", event.target.value)} /></label>
        {editing ? <><p className="save-status" role="status">{status}</p><button className="danger-button" type="button" disabled={deleting} onClick={() => setConfirmDelete(true)}><Trash2 size={17} />Delete {config.singular}</button></> : <button className="primary-button" type="submit">Add {config.singular}</button>}
      </form>
      {confirmDelete && <DeleteChecklistDialog config={config} name={name} busy={deleting} onCancel={() => setConfirmDelete(false)} onConfirm={() => void remove()} />}
    </AppContainer>
  );
}

interface GameEditorForm { name: string; notes: string; }
type GameMutationError = Error & { fields?: Array<{ field: string; message: string }> };

async function gameMutationError(response: Response): Promise<GameMutationError> {
  const body = await response.json().catch(() => null) as { error?: { message?: string; fields?: Array<{ field: string; message: string }> } } | null;
  return Object.assign(new Error(body?.error?.message ?? "Could not save this game."), { fields: body?.error?.fields });
}

export function gameEditorValidation(form: GameEditorForm) {
  const errors: Record<string, string> = {};
  const name = form.name.trim();
  if (!name) errors.name = "Name is required.";
  else if (name.length > 120) errors.name = "Name must be 120 characters or fewer.";
  if (form.notes.length > 600) errors.notes = "Notes must be 600 characters or fewer.";
  return errors;
}

export function DeleteGameDialog({ game, busy, cancel, confirm }: { game: Game; busy: boolean; cancel: () => void; confirm: () => void }) {
  return <DeleteConfirmDialog title={<>Delete “{game.name}”?</>} titleId="delete-game-title" description="This permanently deletes the game. This cannot be undone." descriptionId="delete-game-description" busy={busy} onCancel={cancel} onConfirm={confirm} confirmLabel="Delete game" />;
}

function GameEditorActions({ editing, saving, status, deleting, onDelete }: { editing: boolean; saving: boolean; status: string; deleting: boolean; onDelete: () => void }) {
  if (editing) return <div className="dinner-live-actions"><p className="save-status" role="status">{status}</p><button className="editor-delete-button" type="button" onClick={onDelete} disabled={deleting}><Trash2 size={17} />{deleting ? "Deleting…" : "Delete"}</button></div>;
  return <div className="form-actions dinner-form-actions"><button className="primary-button" type="submit" disabled={saving}>{saving ? "Saving…" : "Add game"}</button></div>;
}

function GameEditor({ id, user, onSignOut }: { id: number | null; user: SwitcherUser; onSignOut: () => Promise<void> }) {
  const editing = id !== null;
  const [form, setForm] = useState<GameEditorForm>({ name: "", notes: "" });
  const [loading, setLoading] = useState(editing);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [pageError, setPageError] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const timer = useRef<number | undefined>(undefined);
  const snapshot = useRef<GameEditorForm>({ name: "", notes: "" });
  const lastSaved = useRef("");
  const deletingRef = useRef(false);
  const leavingRef = useRef(false);
  const queue = useRef<DinnerLiveSaveQueue<GameEditorForm> | null>(null);

  if (!queue.current) {
    queue.current = createDinnerLiveSaveQueue(async value => {
      const response = await realtimeFetch(`/api/games/${id}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(value) });
      if (!response.ok) throw await gameMutationError(response);
    }, {
      pending: () => { if (!deletingRef.current) setStatus("Saving…"); },
      saved: value => {
        lastSaved.current = JSON.stringify(value);
        if (!deletingRef.current) { setStatus("Saved"); setPageError(""); setFieldErrors({}); }
      },
      failed: caught => {
        if (!deletingRef.current) {
          setStatus("");
          setFieldErrors(Object.fromEntries(((caught as GameMutationError)?.fields ?? []).map(field => [field.field, field.message])));
          setPageError(caught instanceof Error ? caught.message : "Could not save this game.");
        }
      },
    });
  }

  useEffect(() => {
    if (!editing) return;
    const controller = new AbortController();
    void (async () => {
      try {
        const response = await fetch(`/api/games/${id}`, { signal: controller.signal });
        if (!response.ok) throw new Error(await responseError(response));
        const data = await response.json() as { game: Game };
        const loaded = { name: data.game.name, notes: data.game.notes };
        snapshot.current = loaded;
        lastSaved.current = JSON.stringify(loaded);
        setForm(loaded);
      } catch (caught) {
        if ((caught as Error).name !== "AbortError") setPageError(caught instanceof Error ? caught.message : "Could not load this game.");
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    })();
    return () => controller.abort();
  }, [editing, id]);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  function validate(value: GameEditorForm) {
    const errors = gameEditorValidation(value);
    setFieldErrors(errors);
    setPageError("");
    if (Object.keys(errors).length > 0) { setStatus(""); return false; }
    return true;
  }

  async function enqueue(value: GameEditorForm, revision?: number) {
    if (JSON.stringify(value) === lastSaved.current && queue.current!.isIdle()) { setStatus("Saved"); return true; }
    return queue.current!.enqueue(value, revision);
  }

  function schedule(value: GameEditorForm) {
    window.clearTimeout(timer.current);
    if (!editing) return;
    if (!validate(value)) { queue.current!.invalidate(); return; }
    const revision = queue.current!.reserve();
    setStatus("Saving…");
    timer.current = window.setTimeout(() => { timer.current = undefined; void enqueue(value, revision); }, 500);
  }

  function setField(field: keyof GameEditorForm, value: string) {
    if (deletingRef.current || leavingRef.current) return;
    const next = { ...snapshot.current, [field]: value };
    snapshot.current = next;
    setForm(next);
    if (editing) schedule(next); else setFieldErrors(current => { const errors = { ...current }; delete errors[field]; return errors; });
  }

  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const current = snapshot.current;
    window.clearTimeout(timer.current);
    if (!validate(current)) { queue.current!.invalidate(); return; }
    if (editing) { const revision = queue.current!.reserve(); setStatus("Saving…"); await enqueue(current, revision); return; }
    setSaving(true);
    setPageError("");
    setFieldErrors({});
    try {
      const response = await realtimeFetch("/api/games", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(current) });
      if (!response.ok) throw await gameMutationError(response);
      goTo("/games");
    } catch (caught) {
      setFieldErrors(Object.fromEntries(((caught as GameMutationError)?.fields ?? []).map(field => [field.field, field.message])));
      setPageError(caught instanceof Error ? caught.message : "Could not save this game.");
      setSaving(false);
    }
  }

  async function back() {
    if (!editing || deletingRef.current || leavingRef.current) return;
    window.clearTimeout(timer.current);
    const current = snapshot.current;
    if (!validate(current)) { queue.current!.invalidate(); return; }
    leavingRef.current = true;
    const revision = queue.current!.reserve();
    setStatus("Saving…");
    if (await enqueue(current, revision)) goTo("/games"); else leavingRef.current = false;
  }

  async function remove() {
    if (!editing || !id || deleting) return;
    deletingRef.current = true;
    setDeleting(true);
    setPageError("");
    setStatus("");
    window.clearTimeout(timer.current);
    queue.current!.invalidate();
    await queue.current!.settle();
    try {
      const response = await realtimeFetch(`/api/games/${id}`, { method: "DELETE" });
      if (!response.ok) throw new Error(await responseError(response));
      goTo("/games");
    } catch (caught) {
      deletingRef.current = false;
      setPageError(caught instanceof Error ? caught.message : "Could not delete this game.");
      setShowDeleteConfirmation(false);
      setDeleting(false);
    }
  }

  const editorTitle = editing ? "Edit game" : "New game";
  if (loading) return <AppContainer className="editor-shell dinner-editor-shell app-games" currentDestination="games" user={user} onSignOut={onSignOut} backHref="/games" backLabel="Back to Games" title={editorTitle} asHeading><div className="dinner-state" role="status"><span className="loading-dot" />Loading game…</div></AppContainer>;
  if (pageError && editing && !form.name) return <AppContainer className="editor-shell dinner-editor-shell app-games" currentDestination="games" user={user} onSignOut={onSignOut} backHref="/games" backLabel="Back to Games" title={editorTitle} asHeading><div className="dinner-state error-state" role="alert"><AlertCircle size={22} /><p>{pageError}</p></div></AppContainer>;
  return (
    <AppContainer className="editor-shell dinner-editor-shell app-games" currentDestination="games" user={user} onSignOut={onSignOut} {...(editing ? { onBack: () => void back() } : { backHref: "/games" })} backLabel="Back to Games" title={editorTitle} asHeading>
      <form className="dinner-form" autoComplete="off" onSubmit={save} noValidate>
        {pageError && <div className="form-error" role="alert"><AlertCircle size={18} />{pageError}</div>}
        <label className="form-field"><span>Name</span><input autoFocus={!editing} name="game-name" autoComplete="off" autoCapitalize="sentences" value={form.name} onChange={event => setField("name", event.target.value)} maxLength={120} placeholder="What should we play?" aria-invalid={Boolean(fieldErrors.name)} aria-describedby={fieldErrors.name ? "game-name-error" : undefined} />{fieldErrors.name && <small id="game-name-error" className="field-error">{fieldErrors.name}</small>}</label>
        <label className="form-field"><span>Notes</span><textarea name="game-notes" autoComplete="off" autoCapitalize="sentences" value={form.notes} onChange={event => setField("notes", event.target.value)} maxLength={600} rows={4} placeholder="Players, setup, or house rules…" aria-invalid={Boolean(fieldErrors.notes)} aria-describedby={fieldErrors.notes ? "game-notes-error" : undefined} />{fieldErrors.notes && <small id="game-notes-error" className="field-error">{fieldErrors.notes}</small>}</label>
        <GameEditorActions editing={editing} saving={saving} status={status} deleting={deleting} onDelete={() => setShowDeleteConfirmation(true)} />
      </form>
      {showDeleteConfirmation && editing && <DeleteGameDialog game={{ id, name: form.name, notes: form.notes, score: 0, skips: 0 }} busy={deleting} cancel={() => !deleting && setShowDeleteConfirmation(false)} confirm={() => void remove()} />}
    </AppContainer>
  );
}

export function GamesApp({ route, user, onSignOut }: { route: string; user: SwitcherUser; onSignOut: () => Promise<void> }) {
  if (route === "/games/new") return <GameEditor id={null} user={user} onSignOut={onSignOut} />;
  const gameEdit = /^\/games\/(\d+)\/edit$/.exec(route);
  if (gameEdit) return <GameEditor id={Number(gameEdit[1])} user={user} onSignOut={onSignOut} />;
  return <GamesPage user={user} onSignOut={onSignOut} />;
}

export function ActivitiesApp({ route, user, onSignOut }: { route: string; user: SwitcherUser; onSignOut: () => Promise<void> }) {
  if (route === "/activities/new") return <ChecklistEditor config={activityConfig} id={null} user={user} onSignOut={onSignOut} />;
  const activityEdit = /^\/activities\/(\d+)\/edit$/.exec(route);
  if (activityEdit) return <ChecklistEditor config={activityConfig} id={Number(activityEdit[1])} user={user} onSignOut={onSignOut} />;
  return <ChecklistPage config={activityConfig} user={user} onSignOut={onSignOut} />;
}
